/**
 * Boli Live - Go Live Page
 *
 * Streamer dashboard for sellers to create auctions and start live streams.
 */

import { useRef, useState, useEffect } from 'react';
import { Mic, MicOff, Video, VideoOff, Upload, ArrowLeft, Copy, CheckCircle, Loader2 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { cn, formatPKR } from '../lib/utils';
import { useAuthStore } from '../lib/stores/auth-store';
import { API_BASE_URL } from '../lib/api-config';

interface StreamCredentials {
  streamKey: string;
  ingestEndpoint: string;
  playbackUrl: string;
}

export default function GoLivePage() {
  const navigate = useNavigate();
  const { isAuthenticated, user } = useAuthStore();
  const videoRef = useRef<HTMLVideoElement>(null);

  const [isCameraOn, setIsCameraOn] = useState(true);
  const [isMicOn, setIsMicOn] = useState(true);
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [startPrice, setStartPrice] = useState('');
  const [duration, setDuration] = useState('15'); // minutes
  const [isCreating, setIsCreating] = useState(false);
  const [isLive, setIsLive] = useState(false);
  const [error, setError] = useState('');
  const [streamCredentials, setStreamCredentials] = useState<StreamCredentials | null>(null);
  const [copiedKey, setCopiedKey] = useState(false);

  // Redirect if not authenticated
  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/profile');
    }
  }, [isAuthenticated, navigate]);

  // Initialize Camera
  useEffect(() => {
    let stream: MediaStream | null = null;

    const startCamera = async () => {
      try {
        if (isCameraOn) {
          stream = await navigator.mediaDevices.getUserMedia({ video: true, audio: isMicOn });
          if (videoRef.current) {
            videoRef.current.srcObject = stream;
          }
        } else {
          if (videoRef.current?.srcObject) {
            const tracks = (videoRef.current.srcObject as MediaStream).getTracks();
            tracks.forEach(track => track.stop());
            videoRef.current.srcObject = null;
          }
        }
      } catch {
        // Error accessing camera
      }
    };

    startCamera();

    return () => {
      if (stream) {
        stream.getTracks().forEach(track => track.stop());
      }
    };
  }, [isCameraOn, isMicOn]);

  const handleGoLive = async () => {
    if (!title || !startPrice) return;

    setIsCreating(true);
    setError('');

    try {
      const token = localStorage.getItem('boli_auth_token');

      // Step 1: Create auction
      const endTimestamp = Date.now() + parseInt(duration) * 60 * 1000;
      const auctionResponse = await fetch(`${API_BASE_URL}/api/collections/auctions/records`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          sellerId: user?.id,
          title,
          description: description || title,
          status: 'scheduled',
          category: 'other',
          startingPrice: parseInt(startPrice),
          currentHighBid: parseInt(startPrice),
          bidCount: 0,
          endTimestamp,
          extensionCount: 0,
          viewerCount: 0,
          images: [],
        }),
      });

      if (!auctionResponse.ok) {
        throw new Error('Failed to create auction');
      }

      const auctionData = await auctionResponse.json();

      // Step 2: Create stream
      const streamResponse = await fetch(`${API_BASE_URL}/api/streams/create`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ auctionId: auctionData.id }),
      });

      if (!streamResponse.ok) {
        throw new Error('Failed to create stream');
      }

      const streamData = await streamResponse.json();
      setStreamCredentials({
        streamKey: streamData.stream.streamKey,
        ingestEndpoint: streamData.stream.ingestEndpoint,
        playbackUrl: streamData.stream.playbackUrl,
      });

      // Step 3: Start stream (marks auction as live)
      await fetch(`${API_BASE_URL}/api/streams/${auctionData.id}/start`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      setIsLive(true);

      // Redirect to auction page after a short delay
      setTimeout(() => {
        navigate(`/auction/${auctionData.id}`);
      }, 3000);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to go live');
      setIsCreating(false);
    }
  };

  const copyStreamKey = () => {
    if (streamCredentials?.streamKey) {
      navigator.clipboard.writeText(streamCredentials.streamKey);
      setCopiedKey(true);
      setTimeout(() => setCopiedKey(false), 2000);
    }
  };

  return (
    <div className="min-h-screen bg-background flex flex-col md:flex-row">
      {/* Left Panel: Stream Preview */}
      <div className="relative w-full md:w-2/3 lg:w-3/4 bg-black aspect-video md:aspect-auto md:h-screen flex items-center justify-center overflow-hidden">

        {/* Camera Preview */}
        <video
          ref={videoRef}
          autoPlay
          muted
          playsInline
          className={cn("w-full h-full object-cover", !isCameraOn && "hidden")}
        />

        {!isCameraOn && (
          <div className="flex flex-col items-center text-muted-foreground">
            <VideoOff className="w-16 h-16 mb-4" />
            <p>Camera is off</p>
          </div>
        )}

        {/* Live indicator */}
        {isLive && (
          <div className="absolute top-4 left-4 bg-red-600 px-3 py-1 rounded text-white text-sm font-bold uppercase tracking-wide flex items-center gap-2">
            <span className="w-2 h-2 bg-white rounded-full animate-pulse" />
            LIVE
          </div>
        )}

        {/* Overlay Controls */}
        <div className="absolute bottom-6 left-0 right-0 flex justify-center gap-4 z-10">
          <button
            onClick={() => setIsMicOn(!isMicOn)}
            className={cn(
              "p-4 rounded-full backdrop-blur-md transition-all",
              isMicOn ? "bg-white/20 text-white hover:bg-white/30" : "bg-red-500 text-white"
            )}
          >
            {isMicOn ? <Mic className="w-6 h-6" /> : <MicOff className="w-6 h-6" />}
          </button>

          <button
            onClick={() => setIsCameraOn(!isCameraOn)}
            className={cn(
              "p-4 rounded-full backdrop-blur-md transition-all",
              isCameraOn ? "bg-white/20 text-white hover:bg-white/30" : "bg-red-500 text-white"
            )}
          >
            {isCameraOn ? <Video className="w-6 h-6" /> : <VideoOff className="w-6 h-6" />}
          </button>
        </div>

        {/* Back Button */}
        <button
          onClick={() => navigate(-1)}
          className="absolute top-4 left-4 p-2 rounded-full bg-black/20 text-white md:hidden"
        >
          <ArrowLeft className="w-6 h-6" />
        </button>
      </div>

      {/* Right Panel: Controls */}
      <div className="flex-1 bg-card border-l border-border p-6 flex flex-col h-screen overflow-y-auto">
        <div className="mb-8">
          <button
            onClick={() => navigate(-1)}
            className="hidden md:flex items-center gap-2 text-muted-foreground hover:text-foreground mb-4 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Back
          </button>
          <h1 className="text-2xl font-bold text-foreground mb-2">
            {isLive ? 'You are Live!' : 'Setup Auction'}
          </h1>
          <p className="text-muted-foreground text-sm">
            {isLive
              ? 'Your auction is now live. Bidders can start bidding!'
              : 'Fill in the details before going live.'}
          </p>
        </div>

        {isLive && streamCredentials ? (
          /* Stream credentials panel */
          <div className="space-y-6 flex-1">
            <div className="p-4 bg-green-50 border border-green-200 rounded-xl">
              <div className="flex items-center gap-2 text-green-700 mb-2">
                <CheckCircle className="w-5 h-5" />
                <span className="font-semibold">Stream Started</span>
              </div>
              <p className="text-sm text-green-600">
                Redirecting to auction room...
              </p>
            </div>

            <div className="space-y-4">
              <div>
                <label className="text-sm font-medium text-foreground">Stream Key</label>
                <div className="flex items-center gap-2 mt-1">
                  <input
                    type="text"
                    value={streamCredentials.streamKey}
                    readOnly
                    className="flex-1 bg-secondary border border-input rounded-lg px-4 py-2 text-foreground font-mono text-sm"
                  />
                  <button
                    onClick={copyStreamKey}
                    className="p-2 rounded-lg bg-secondary hover:bg-secondary/80 transition-colors"
                  >
                    {copiedKey ? (
                      <CheckCircle className="w-5 h-5 text-green-600" />
                    ) : (
                      <Copy className="w-5 h-5 text-muted-foreground" />
                    )}
                  </button>
                </div>
              </div>

              <div>
                <label className="text-sm font-medium text-foreground">Ingest Endpoint</label>
                <input
                  type="text"
                  value={streamCredentials.ingestEndpoint}
                  readOnly
                  className="w-full mt-1 bg-secondary border border-input rounded-lg px-4 py-2 text-foreground font-mono text-sm"
                />
              </div>
            </div>
          </div>
        ) : (
          /* Setup form */
          <div className="space-y-6 flex-1">
            {/* Title Input */}
            <div className="space-y-2">
              <label className="text-sm font-medium text-foreground">Auction Title</label>
              <input
                type="text"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="e.g. Vintage Rolex Watch 1980"
                className="w-full bg-secondary border border-input rounded-lg px-4 py-3 text-foreground placeholder:text-muted-foreground focus:ring-2 focus:ring-primary focus:outline-none"
                disabled={isCreating}
              />
            </div>

            {/* Description Input */}
            <div className="space-y-2">
              <label className="text-sm font-medium text-foreground">Description (optional)</label>
              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Describe your item..."
                rows={2}
                className="w-full bg-secondary border border-input rounded-lg px-4 py-3 text-foreground placeholder:text-muted-foreground focus:ring-2 focus:ring-primary focus:outline-none resize-none"
                disabled={isCreating}
              />
            </div>

            {/* Price and Duration */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <label className="text-sm font-medium text-foreground">Starting Price (PKR)</label>
                <div className="relative">
                  <span className="absolute left-4 top-1/2 -translate-y-1/2 text-muted-foreground font-semibold">Rs.</span>
                  <input
                    type="number"
                    value={startPrice}
                    onChange={(e) => setStartPrice(e.target.value)}
                    placeholder="0"
                    className="w-full bg-secondary border border-input rounded-lg pl-12 pr-4 py-3 text-foreground font-mono focus:ring-2 focus:ring-primary focus:outline-none"
                    disabled={isCreating}
                  />
                </div>
              </div>

              <div className="space-y-2">
                <label className="text-sm font-medium text-foreground">Duration</label>
                <select
                  value={duration}
                  onChange={(e) => setDuration(e.target.value)}
                  className="w-full bg-secondary border border-input rounded-lg px-4 py-3 text-foreground focus:ring-2 focus:ring-primary focus:outline-none"
                  disabled={isCreating}
                >
                  <option value="5">5 minutes</option>
                  <option value="10">10 minutes</option>
                  <option value="15">15 minutes</option>
                  <option value="30">30 minutes</option>
                  <option value="60">1 hour</option>
                </select>
              </div>
            </div>

            {/* Thumbnail Upload (Mock) */}
            <div className="border-2 border-dashed border-border rounded-xl p-8 flex flex-col items-center justify-center text-center cursor-pointer hover:bg-secondary/50 transition-colors">
              <div className="w-12 h-12 rounded-full bg-secondary flex items-center justify-center mb-3">
                <Upload className="w-5 h-5 text-muted-foreground" />
              </div>
              <p className="text-sm font-medium text-foreground">Upload Thumbnail</p>
              <p className="text-xs text-muted-foreground mt-1">1080x1080 recommended</p>
            </div>

            {error && (
              <p className="text-sm text-red-500 text-center">{error}</p>
            )}
          </div>
        )}

        {/* Action Button */}
        {!isLive && (
          <div className="mt-8 pt-6 border-t border-border">
            <button
              onClick={handleGoLive}
              disabled={!title || !startPrice || isCreating}
              className={cn(
                "w-full py-4 rounded-xl font-bold text-lg shadow-lg transition-all flex items-center justify-center gap-2",
                isCreating
                  ? "bg-muted text-muted-foreground cursor-not-allowed"
                  : "bg-primary text-primary-foreground hover:bg-primary/90 active:scale-[0.98]"
              )}
            >
              {isCreating ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  Creating Auction...
                </>
              ) : (
                "Go Live Now"
              )}
            </button>

            {startPrice && (
              <p className="text-center text-sm text-muted-foreground mt-3">
                Starting at {formatPKR(parseInt(startPrice))}
              </p>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
